import { ToastProgrammatic as Toast } from 'buefy';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import {
  mapActions,
  mapState,
  mapMutations,
  mapGetters,
} from 'vuex';
import useVuelidate from '@vuelidate/core';

import { GET_ACTIVE_MEMBERS, SET_MEMBERS, SORT_WITH_SELECTED_MEMBER } from '@/store/member/constants';
import { UPDATE_DRAFT_ORDER } from '@/store/orders/constants';
import {
  ALLOW_EDIT_TYPES,
  DEFAULT_PER_PAGE,
  FIRST_PAGE,
  ORDER_STATUS_WITH_TYPE_TEXT,
  PER_PAGE_OPTIONS,
} from '@/utils/constants';
import { required } from '@/utils/validators';

import ExpectedOrderCount from '../ExpectedOrderCount/ExpectedOrderCount.vue';
import JoinedMembers from '../JoinedMembers/JoinedMembers.vue';
import MemberTable from '../../MemberTable/MemberTable.vue';

export default {
  name: 'MemberSelector',
  components: {
    ExpectedOrderCount,
    JoinedMembers,
    MemberTable,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  props: {
    allowEditType: {
      default: ALLOW_EDIT_TYPES.ALL,
      type: Number,
      validator: (val) => Object.values(ALLOW_EDIT_TYPES).includes(val),
    },
    orderType: {
      default: ORDER_STATUS_WITH_TYPE_TEXT.DRAFT,
      type: String,
      validator: (val) => Object.values(ORDER_STATUS_WITH_TYPE_TEXT).includes(val),
    },
  },
  data() {
    return {
      keyword: '',
      members: [],
      needGetMemberDepartments: [],
      originSelectedData: {},
      page: 1,
      pageCount: 0,
      perPage: DEFAULT_PER_PAGE,
      resolvedDepartmentIds: [],
      search: '',
      selectedMembers: {},
      sortField: 'id',
      sortOrder: 'desc',
    };
  },
  computed: {
    ...mapGetters('orders', ['selectedDepartmentIds', 'originSelectedMembers']),
    ...mapState('orders', ['departments']),
    ...mapState('member', {
      loading: ({ isGettingMembers }) => isGettingMembers,
      membersList: ({ members }) => members,
    }),
    allowEditAll() {
      return this.allowEditType === ALLOW_EDIT_TYPES.ALL;
    },
    allMembers() {
      return this.membersList?.data || [];
    },
    lastPage() {
      return this.pagination.total_page || 1;
    },
    noMorePages() {
      return this.page >= this.lastPage;
    },
    pagination() {
      return this.membersList?.pagination || {};
    },
    statistics() {
      const total = this.departments?.reduce((pre, cur) => pre + (cur?.member_count || 0), 0) || 0;
      let joined = 0;
      const { includes = {}, excludes = {} } = this.selectedMembers;
      Object.keys(excludes).forEach((departmentId) => {
        const count = this.getDepartmentCount(departmentId);
        if (!excludes[departmentId]?.length) {
          joined += count;
        } else {
          joined += (count - excludes[departmentId].length);
        }
      });
      Object.keys(includes).forEach((departmentId) => {
        joined += includes[departmentId].length;
      });

      return { joined, total };
    },
    totalPage() {
      return this.pagination?.total_page || 0;
    },
    perPageOptions() {
      return PER_PAGE_OPTIONS;
    },
    total() {
      return this.pagination?.total || 0;
    },
    searchParam() {
      const search = {};
      if (this.needGetMemberDepartments.length) {
        search.department_ids = this.needGetMemberDepartments;
      }
      if (this.keyword) {
        search.name = this.keyword;
        search.account = this.keyword;
        search.serial = this.keyword;
        search.ext = this.keyword;
      }
      return search;
    },
  },
  created() {
    this.initData();
  },
  async mounted() {
    this.$nextTick(() => {
      this.pageCount += 1;
    });
    this.selectedMembers = cloneDeep(this.originSelectedMembers);
    if (!this.allowEditAll) {
      this.originSelectedData = cloneDeep(this.originSelectedMembers);
    }
  },
  watch: {
    allMembers: {
      deep: true,
      handler(newVal, oldVal) {
        if (!isEqual(newVal, oldVal)) {
          this.members = cloneDeep(newVal);
        }
      },
    },
    members(newVal) {
      // NOTE: Don't modify user's selection except department_ids checkboxes are all empty
      if (isEmpty(this.searchParam?.department_ids)) {
        const departmentIds = newVal?.reduce((acc, { department_id: departmentId }) => {
          acc.add(departmentId);
          return acc;
        }, new Set()) || [];
        this.resolvedDepartmentIds = Array.from(departmentIds);
      }
    },
    page() {
      this.getMembers();
    },
    perPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setPageOrGetData();
      }
    },
    selectedDepartmentIds(newVal) {
      this.resolvedDepartmentIds = newVal ? cloneDeep(newVal) : [];
    },
  },
  beforeDestroy() {
    this.initData();
  },
  methods: {
    ...mapActions('member', {
      getActiveMembers: GET_ACTIVE_MEMBERS,
      sortWithSelectedMember: SORT_WITH_SELECTED_MEMBER,
    }),
    ...mapMutations('member', {
      setMembers: SET_MEMBERS,
    }),
    ...mapMutations('orders', {
      updateDraftOrder: UPDATE_DRAFT_ORDER,
    }),
    clearAllSelected() {
      this.selectedMembers = cloneDeep(this.originSelectedData || {});
      this.updateDraftOrder({
        selectedMembers: this.originSelectedData || {},
      });
    },
    async getMembers() {
      try {
        if (this.sortField === 'selected') {
          this.sortMembersBySelected();
          return;
        }
        await this.getActiveMembers(
          {
            order: this.sortOrder,
            page: this.page,
            per_page: this.perPage,
            search: this.searchParam,
            sort_by: this.sortField,
          },
        );
      } catch {
        Toast.open({
          message: '無法載入人員清單，請稍後再試',
          type: 'is-danger',
        });
      }
    },
    async sortMembersBySelected() {
      try {
        await this.sortWithSelectedMember(
          {
            order: this.sortOrder,
            page: this.page,
            per_page: this.perPage,
            search: this.searchParam,
            selected_members: this.selectedMembers,
          },
        );
      } catch {
        Toast.open({
          message: '無法載入人員清單，請稍後再試',
          type: 'is-danger',
        });
      }
    },
    initData() {
      this.setMembers({});
      Object.assign(this.$data, this.$options.data.apply(this));
      this.resolvedDepartmentIds = cloneDeep(this.selectedDepartmentIds);
    },
    onSelectedDepartments(targets) {
      const departmentIds = targets
        .filter((id) => id !== 'null')
        .map((id) => +id);
      this.needGetMemberDepartments = departmentIds;
      if (departmentIds.length) {
        this.page = 1;
        this.getMembers();
      } else {
        this.setMembers({});
      }
      this.updateDraftOrder({
        departmentIds,
      });
    },
    getDepartmentCount(departmentId) {
      const department = this.departments.find((d) => String(d.id) === String(departmentId));
      return department?.member_count || 0;
    },
    setAllSelectedMembers(data) {
      this.selectedMembers = data || {};
    },
    setSelectedMember(member) {
      const { department_id: departmentId, id: targetMemberId } = member;
      const { excludes = {}, includes = {} } = this.selectedMembers;
      const needExclude = !!excludes[departmentId];
      const includesIndex = includes[departmentId]?.findIndex((id) => id === targetMemberId) ?? -1;
      const excludesIndex = excludes[departmentId]?.findIndex((id) => id === targetMemberId) ?? -1;
      if (excludesIndex > -1) {
        excludes[departmentId].splice(excludesIndex, 1);
      } else if (includesIndex > -1) {
        includes[departmentId].splice(includesIndex, 1);
      } else if (needExclude) {
        excludes[departmentId].push(targetMemberId);
      } else if (!includes[departmentId]) {
        includes[departmentId] = [targetMemberId];
      } else {
        includes[departmentId].push(targetMemberId);
      }

      this.selectedMembers = { excludes, includes };

      this.updateDraftOrder({
        selectedMembers: cloneDeep(this.selectedMembers),
      });
    },
    removeSelectedDepartment(departmentId) {
      const { excludes = {}, includes = {} } = this.selectedMembers;
      const { excludes: originExcludes, includes: originIncludes } = this.originSelectedData;
      if (originExcludes?.[departmentId]) {
        excludes[departmentId] = cloneDeep(originExcludes[departmentId]);
      } else {
        delete excludes[departmentId];
      }
      if (originIncludes?.[departmentId]) {
        includes[departmentId] = cloneDeep(originIncludes[departmentId]);
      } else {
        delete includes[departmentId];
      }
      this.selectedMembers = { excludes, includes };
      this.updateDraftOrder({
        selectedMembers: cloneDeep(this.selectedMembers),
      });
    },
    clearSearch() {
      this.keyword = '';
      this.getMembers();
    },
    setPageOrGetData() {
      this.members = [];
      if (this.page !== FIRST_PAGE) {
        this.page = FIRST_PAGE;
      } else {
        this.getMembers();
      }
    },
    onSort(field, order) {
      this.sortField = field;
      this.sortOrder = order;
      this.getMembers();
    },
  },
  validations() {
    return {
      originSelectedMembers: {
        required: required('人員'),
        mustHaveIncludesOrExcludes: (value) => {
          if (!value) return false;
          const { includes = {}, excludes = {} } = value;
          return Object.values(includes)
            .some((member) => member.length > 0)
            || Object.keys(excludes).length > 0;
        },
      },
    };
  },
};
